import { useState } from 'react';
import { Alert, AlertTitle, Dialog, DialogTitle, DialogActions, IconButton, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import { useSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import CombinationRuleForm from './CombinationRuleForm';
import { TimekeeperService } from 'services/TimekeeperService';

interface OvertimeRule {
    overtime_rule_id: number;
    name: string;
}

interface Zone {
    zone_id: number;
    name: string;
}

interface ScheduleDesviationRule {
    schedule_desviation_rule_id: number;
    name: string;
}

interface CombinationRuleFormData {
    combination_rule_id?: number | string | null;
    name: string;
    description: string;
    overtimeSelect: number[];
    zoneSelect: number[];
    scheduleDesviationSelect: number[];
    overtime_rules: OvertimeRule[];
    zones: Zone[];
    schedule_desviation_rules: ScheduleDesviationRule[];
}

interface CombinationRuleRow {
    id: number;
    name: string;
    description?: string;
    overtime_rules?: OvertimeRule[];
    zones?: Zone[];
    schedule_desviation_rules?: ScheduleDesviationRule[];
}

interface EditCombinationsProps {
    open: boolean;
    setOpenEdit: (open: boolean) => void;
    rows: CombinationRuleRow[];
    select: CombinationRuleRow | null;
    reloading: () => void;
}

const tk = new TimekeeperService();

const EditCombinations = ({ open, setOpenEdit, rows, select, reloading }: EditCombinationsProps) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState<boolean>(false);
    const [alert, setAlert] = useState<boolean>(false);

    const [formData, setFormData] = useState<CombinationRuleFormData>({
        combination_rule_id: null,
        name: '',
        description: '',
        overtimeSelect: [],
        zoneSelect: [],
        scheduleDesviationSelect: [],
        overtime_rules: [],
        zones: [],
        schedule_desviation_rules: [],
    });

    const cleanForm = (): void => {
        setFormData({
            combination_rule_id: null,
            name: '',
            description: '',
            overtimeSelect: [],
            zoneSelect: [],
            scheduleDesviationSelect: [],
            overtime_rules: [],
            zones: [],
            schedule_desviation_rules: [],
        });
        setOpenEdit(false);
        setAlert(false);
    };

    const existName = (name: string): boolean => {
        if (!name || name.trim() === '') return false;

        // Si el nombre no ha cambiado del original, no debe considerarse como existente
        if (select && String(select.name).toLowerCase() === String(name).toLowerCase().trim()) {
            return false;
        }

        // Verifica si existe en otros registros
        return rows.some((row) => String(row.name).toLowerCase() === String(name).toLowerCase().trim());
    };

    const changes = (): void => {
        if (!select) {
            cleanForm();
            return;
        }

        const hasChanges =
            formData.name !== select.name ||
            formData.description !== select.description ||
            JSON.stringify(formData.overtimeSelect) !== JSON.stringify(select.overtime_rules?.map((o) => o.overtime_rule_id) || []) ||
            JSON.stringify(formData.zoneSelect) !== JSON.stringify(select.zones?.map((z) => z.zone_id) || []) ||
            JSON.stringify(formData.scheduleDesviationSelect) !== JSON.stringify(select.schedule_desviation_rules?.map((s) => s.schedule_desviation_rule_id) || []);

        hasChanges ? setAlert(true) : cleanForm();
    };

    const disabled = (): boolean => {
        if (!formData.name || formData.name.trim() === '' || existName(formData.name)) return true;

        const selectionCount = [formData.overtimeSelect, formData.zoneSelect, formData.scheduleDesviationSelect].filter((array) => array.length > 0).length;

        return selectionCount < 1;
    };

    const updateCombinationRule = async (): Promise<void> => {
        setLoading(true);
        try {
            const data = {
                combination_rule_id: formData.combination_rule_id,
                name: formData.name,
                description: formData.description,
                payment_method: true,
                zones: formData.zones.map((zone) => zone.zone_id),
                overtime_rules: formData.overtime_rules.map((overtime) => overtime.overtime_rule_id),
                schedule_desviation_rules: formData.schedule_desviation_rules.map((schedule_desviation_rule) => schedule_desviation_rule.schedule_desviation_rule_id),
            };

            await tk.combination_rule.updateCombination(data as any);

            enqueueSnackbar(t('settings.combinations.dialog.alerts.edit.success'), {
                variant: 'success',
            });
            cleanForm();
            reloading();
        } catch (error) {
            console.error('Error updating combination:', error);
            enqueueSnackbar(t('settings.combinations.dialog.alerts.edit.error'), {
                variant: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Dialog
                maxWidth="md"
                fullWidth
                open={open}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        return;
                    }
                    changes();
                }}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">{t('settings.combinations.dialog.titles.edit')}</DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={() => changes()}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
                <Box sx={{ top: 0, zIndex: 10, pb: 1 }}>
                    {alert && (
                        <Alert
                            variant="filled"
                            severity="warning"
                            sx={{ mt: 2 }}
                            action={
                                <>
                                    <Button onClick={() => setAlert(false)} color="inherit" size="small" variant="text" disableElevation>
                                        {t('general.alerts.unsaved_changes.buttons.yes')}
                                    </Button>

                                    <Button onClick={() => cleanForm()} color="inherit" size="small" variant="text" disableElevation>
                                        {t('general.alerts.unsaved_changes.buttons.no')}
                                    </Button>
                                </>
                            }
                        >
                            <AlertTitle>{t('general.alerts.unsaved_changes.message')}</AlertTitle>
                        </Alert>
                    )}
                </Box>
                <CombinationRuleForm mode="edit" select={select} rows={rows} formData={formData} setFormData={setFormData} />
                <DialogActions>
                    <Button variant="contained" color="error" onClick={() => changes()}>
                        {t('settings.combinations.dialog.buttons.cancel')}
                    </Button>
                    <Button variant="contained" onClick={() => updateCombinationRule()} disabled={disabled()} loading={loading}>
                        {t('settings.combinations.dialog.buttons.save')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export { EditCombinations };
